import { DataField } from "@iventis/domain-model/model/dataField";
import { FormulaOperator } from "@iventis/domain-model/model/formulaOperator";
import { FormulaTokenType } from "@iventis/domain-model/model/formulaTokenType";
import { createLog } from "@iventis/observability-and-monitoring";

type FormulaUnexpectedReasonCode =
    | "FORMULA_INVALID_TARGET_TYPE"
    | "FORMULA_INVALID_TOKEN_SEQUENCE"
    | "FORMULA_DIVISION_BY_ZERO"
    | "FORMULA_UNKNOWN_NUMBER_OPERATOR"
    | "FORMULA_UNSUPPORTED_TIME_OPERATOR"
    | "FORMULA_UNSUPPORTED_NUMBER_OPERAND_TOKEN"
    | "FORMULA_UNSUPPORTED_TIME_OPERAND_TOKEN"
    | "FORMULA_MISSING_REFERENCED_FIELD"
    | "FORMULA_MISSING_REFERENCED_VALUE"
    | "FORMULA_REFERENCED_FIELD_TYPE_MISMATCH"
    | "FORMULA_LIST_PARENT_FIELD_INVALID"
    | "FORMULA_LIST_PROPERTY_INVALID"
    | "FORMULA_MISSING_SELECTED_LIST_ITEM"
    | "FORMULA_MISSING_LIST_PROPERTY_VALUE"
    | "FORMULA_SELECTED_LIST_ITEM_MISSING"
    | "FORMULA_INVALID_TIME_LITERAL_TOKEN"
    | "FORMULA_UNEXPECTED_EXCEPTION";

type FormulaUnexpectedSeverity = "warn" | "error";
type FormulaEvaluatorKind = "dispatch" | "number" | "time";

interface FormulaUnexpectedIssueInput {
    reasonCode: FormulaUnexpectedReasonCode;
    severity: FormulaUnexpectedSeverity;
    evaluator: FormulaEvaluatorKind;
    tokenIndex?: number;
    tokenType?: FormulaTokenType;
    operator?: FormulaOperator;
    dataFieldId?: string;
    propertyId?: string;
    detail?: string;
}

export interface FormulaEvaluationDiagnostics {
    recordUnexpectedIssue(input: FormulaUnexpectedIssueInput): void;
    flushUnexpectedIssues(): void;
}

type UnexpectedIssue = FormulaUnexpectedIssueInput & { occurrenceCount: number };

const FORMULA_LOG_DEDUPLICATION_WINDOW_MS = 5000;
const recentUnexpectedIssueExpirations = new Map<string, number>();

export function createFormulaEvaluationDiagnostics(calculatedDataField: DataField, formulaLength: number): FormulaEvaluationDiagnostics {
    const unexpectedIssuesBySignature = new Map<string, UnexpectedIssue>();
    return {
        recordUnexpectedIssue(input) {
            const signature = createUnexpectedIssueSignature(calculatedDataField.id, input);
            const existingIssue = unexpectedIssuesBySignature.get(signature);
            if (existingIssue != null) {
                existingIssue.occurrenceCount += 1;
                return;
            }
            unexpectedIssuesBySignature.set(signature, { ...input, occurrenceCount: 1 });
        },
        flushUnexpectedIssues() {
            for (const [signature, issue] of unexpectedIssuesBySignature.entries()) {
                if (!shouldEmitUnexpectedIssue(signature)) {
                    continue;
                }
                createLog("Formula evaluation encountered unexpected state", issue.severity, buildUnexpectedIssuePayload(calculatedDataField, formulaLength, issue));
            }
        },
    };
}

function createUnexpectedIssueSignature(calculatedFieldId: string, issue: FormulaUnexpectedIssueInput): string {
    return [
        calculatedFieldId,
        issue.reasonCode,
        issue.evaluator,
        issue.tokenIndex ?? "",
        issue.tokenType ?? "",
        issue.operator ?? "",
        issue.dataFieldId ?? "",
        issue.propertyId ?? "",
        issue.detail ?? "",
    ].join("|");
}

function shouldEmitUnexpectedIssue(signature: string): boolean {
    const now = Date.now();
    // Since expiresAt = now + FORMULA_LOG_DEDUPLICATION_WINDOW_MS at insertion time, and the map preserves
    // insertion order, entries are in ascending expiry order. We can therefore stop sweeping as soon as we
    // reach the first non-expired entry instead of scanning the whole cache on every lookup.
    for (const [cachedSignature, expiresAt] of recentUnexpectedIssueExpirations.entries()) {
        if (expiresAt > now) {
            break;
        }
        recentUnexpectedIssueExpirations.delete(cachedSignature);
    }
    const existingExpiry = recentUnexpectedIssueExpirations.get(signature);
    if (existingExpiry != null && existingExpiry > now) {
        return false;
    }
    // Re-insert (delete then set) so the entry moves to the end, keeping ascending expiry order intact.
    recentUnexpectedIssueExpirations.delete(signature);
    recentUnexpectedIssueExpirations.set(signature, now + FORMULA_LOG_DEDUPLICATION_WINDOW_MS);
    return true;
}

function buildUnexpectedIssuePayload(calculatedDataField: DataField, formulaLength: number, issue: UnexpectedIssue): Record<string, string | number | boolean> {
    const payload: Record<string, string | number | boolean> = {
        calculatedFieldId: calculatedDataField.id,
        calculatedFieldName: calculatedDataField.name,
        calculatedFieldType: String(calculatedDataField.type),
        formulaLength,
        evaluator: issue.evaluator,
        reasonCode: issue.reasonCode,
        occurrenceCount: issue.occurrenceCount,
    };
    if (issue.tokenIndex !== undefined) {
        payload.tokenIndex = issue.tokenIndex;
    }
    if (issue.tokenType !== undefined) {
        payload.tokenType = String(issue.tokenType);
    }
    if (issue.operator !== undefined) {
        payload.operator = String(issue.operator);
    }
    if (issue.dataFieldId !== undefined) {
        payload.dataFieldId = issue.dataFieldId;
    }
    if (issue.propertyId !== undefined) {
        payload.propertyId = issue.propertyId;
    }
    if (issue.detail !== undefined) {
        payload.detail = issue.detail;
    }
    return payload;
}

/** For tests only. Clears the global log dedupe cache in this module. */
export function resetFormulaEvaluationLogDeduplicationForTests(): void {
    recentUnexpectedIssueExpirations.clear();
}
